import { apiGetOrNull, apiRequest } from '@/domains/api';
import type {
  IBindInternalField,
  ICreateInternalField,
  IInternalFieldDetail,
  IInternalFieldGroup,
  IInternalFieldOrder,
  IUpdateInternalField,
  IWriteInternalFieldGroup,
  IWriteInternalFieldOption
} from './internal-catalogue-contracts';

/**
 * survey-api's internal-field **catalogue** — the ten routes of SR-209 §8
 * (`InternalFieldEndpoints.cs`), which author what a staff field *is*: the field, its options,
 * a survey's sections and a survey's placement of its fields. The sole reader of them.
 *
 * **Its own module beside `internal-fields-api.ts`, as survey-api has its own file.** That one
 * reads and writes **values**, under `internal_fields.read` and `internal_fields.write`, from
 * every staff screen; this one is `internal_fields.author`, **admin alone, reads included** —
 * survey-api's ruling, because a staff `GET` here would list every field name and option label,
 * the admin-marked ones among them, where §11.1's schema applies the access filter. One module
 * per policy keeps a staff screen from importing a call it must never make.
 *
 * **Everything is written whole, and nothing is version-owned.** Options are a membership, a
 * survey's groups a block, a survey's placement one ordered list; there is no `PATCH`, no
 * `If-Match` and no `ETag` — last write wins, the value half's ruling (§18.1) applied to the
 * catalogue. A caller that wants to change one option sends the list it has just read.
 *
 * **No module-level cache.** `internal-fields-api.ts` holds each survey's schema for the page
 * load because it is read by every part of a hub and changes only when an author writes. These
 * reads are the author writing: a cached list would show the author their own change missing.
 *
 * **Every path segment is encoded, and a dot segment never sent** — `internal-fields-api.ts`'
 * rule and its reason: the URL parser behind `fetch` removes `.` and `..`, `%2e` included, so
 * such a code would reach another admin route carrying the caller's bearer. A field code cannot
 * be one by survey-api's own shape rule (§8.4), but the codes here come from a form.
 */

const BASE = '/api/v1/admin';

/** Values that could only reach a different route — see the module's comment. */
const NOT_A_SEGMENT = new Set(['', '.', '..']);

function segment(value: string): string {
  if (NOT_A_SEGMENT.has(value)) {
    throw new Error(`'${value}' cannot be sent as a path segment`);
  }
  return encodeURIComponent(value);
}

function fieldPath(code: string): string {
  return `${BASE}/internal-fields/${segment(code)}`;
}

function surveyPath(surveyCode: string): string {
  return `${BASE}/surveys/${segment(surveyCode)}/internal-fields`;
}

export const internalCatalogueApi = {
  /**
   * The authoring view of the catalogue — every field with its options and its bindings,
   * unpaged. `surveyCode` narrows it to the fields bound to one survey; `includeRetired` adds
   * the retired ones, which are absent by default.
   *
   * Unpaged is survey-api's decision and not an oversight: production holds a few hundred
   * fields, and a silent truncation would be the worse failure. The one import so far left 738.
   */
  list(
    query: { surveyCode?: string; includeRetired?: boolean } = {},
    signal?: AbortSignal
  ): Promise<IInternalFieldDetail[]> {
    const params = new URLSearchParams();
    if (query.surveyCode !== undefined) params.append('surveyCode', query.surveyCode);
    if (query.includeRetired === true) params.append('includeRetired', 'true');
    const rendered = params.toString();
    return apiRequest<IInternalFieldDetail[]>(
      `${BASE}/internal-fields${rendered ? `?${rendered}` : ''}`,
      { signal }
    );
  },

  /** One field, or `null` where the catalogue holds no such code. */
  get(code: string, signal?: AbortSignal): Promise<Nullable<IInternalFieldDetail>> {
    if (NOT_A_SEGMENT.has(code)) return Promise.resolve(null);
    return apiGetOrNull<IInternalFieldDetail>(fieldPath(code), { signal });
  },

  /**
   * Create a field — 201 with the field as stored.
   *
   * Refusals, each said at its own target: **400 `invalid-code`** for a code failing
   * `^[a-z0-9]+([._][a-z0-9]+)*$`; **409 `duplicate-code`**; **400 `unknown-member`** for an
   * unknown type, scope or access; **400 `invalid-options`** for options on a type that takes
   * none, or none on `select` / `multi_select`.
   */
  create(body: ICreateInternalField): Promise<IInternalFieldDetail> {
    return apiRequest<IInternalFieldDetail>(`${BASE}/internal-fields`, {
      method: 'POST',
      body
    });
  },

  /**
   * The whole editable block — name, access, constraint, retired — 200 with the field as
   * stored. `type` and `scope` travel as an assertion the server checks: a differing value is
   * **400 `immutable-member`**, and omitting them asserts nothing.
   *
   * Retiring never refuses. A retired field leaves the staff form and stops accepting values,
   * and the values it already holds keep resolving their labels.
   */
  update(code: string, body: IUpdateInternalField): Promise<IInternalFieldDetail> {
    return apiRequest<IInternalFieldDetail>(fieldPath(code), { method: 'PUT', body });
  },

  /**
   * Remove a field — 204, or **409 `field-in-use`** naming the count of values that still hold
   * it. That is the usual answer for anything a record has been filled in with, and retiring is
   * what the refusal points at.
   */
  async remove(code: string): Promise<void> {
    await apiRequest<void>(fieldPath(code), { method: 'DELETE' });
  },

  /**
   * The whole option membership, in order — 200 with the field. The array position is the
   * ordinal. A stored option the body omits is **gone**, unless some value still names it, in
   * which case the write is refused **400 `option-in-use`** and retiring it is the repair.
   */
  setOptions(code: string, options: IWriteInternalFieldOption[]): Promise<IInternalFieldDetail> {
    return apiRequest<IInternalFieldDetail>(`${fieldPath(code)}/options`, {
      method: 'PUT',
      body: options
    });
  },

  /**
   * The lifecycle class and worklist handling of the options named, by their codes as stored
   * (survey-api's reinstatement design §2.4) — 200 with the field; every other option untouched,
   * a null lifetime clearing one's class. 400 by path (`[2].option`) for an option the field
   * lacks or a class survey-api does not know.
   */
  setOptionClasses(
    code: string,
    classes: { option: string; lifetime: Nullable<string>; handling: Nullable<string> }[]
  ): Promise<IInternalFieldDetail> {
    return apiRequest<IInternalFieldDetail>(`${fieldPath(code)}/option-classes`, {
      method: 'PUT',
      body: classes
    });
  },

  /**
   * Bind the field to a survey, or rebind it — 200 with the field. A `group` the survey does
   * not have is **400 `unknown-group`**.
   */
  bind(
    code: string,
    surveyCode: string,
    body: IBindInternalField = {}
  ): Promise<IInternalFieldDetail> {
    return apiRequest<IInternalFieldDetail>(
      `${fieldPath(code)}/surveys/${segment(surveyCode)}`,
      { method: 'PUT', body }
    );
  },

  /**
   * Unbind it — 204, or **409 `binding-in-use`** while a value exists for that survey. The
   * field keeps every other binding.
   */
  async unbind(code: string, surveyCode: string): Promise<void> {
    await apiRequest<void>(`${fieldPath(code)}/surveys/${segment(surveyCode)}`, {
      method: 'DELETE'
    });
  },

  /**
   * One survey's sections, whole — 200 with them in order, the array position the ordinal.
   * Dropping a group some binding still sits in is **400 `group-in-use`**, naming the bindings;
   * repeating a code is **400 `duplicate-group`**.
   */
  setGroups(
    surveyCode: string,
    groups: IWriteInternalFieldGroup[]
  ): Promise<IInternalFieldGroup[]> {
    return apiRequest<IInternalFieldGroup[]>(`${surveyPath(surveyCode)}/groups`, {
      method: 'PUT',
      body: groups
    });
  },

  /**
   * One survey's placement — the only route that moves a field on a form. The body is **every**
   * field bound to the survey, in the order it is to be drawn; omitting one, or naming one that
   * is not bound, is **400 `invalid-order`**. A field's group is the binding's and is not
   * changed here.
   */
  reorder(surveyCode: string, fields: string[]): Promise<IInternalFieldOrder[]> {
    return apiRequest<IInternalFieldOrder[]>(`${surveyPath(surveyCode)}/order`, {
      method: 'PUT',
      body: { fields }
    });
  }
};
